import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { HEAD_ACTION_SHAPE } from '@/components/admin/sessions/export-menu';
import { ReviewTrial } from '@/components/admin/sessions/review/review-trial';
import { plural } from '@/components/admin/sessions/walk/walk-labels';
import { reviewFullDay } from '@/lib/review-labels';
import { personFirstName } from '@/lib/review-transcript';
import { sessionStatusLabel, sessionTime } from '@/lib/session-labels';
import type { SessionRow } from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';

/**
 * The head of the review (relecture.md §4.1): the breadcrumb, the title
 * « Relecture · Session de Camille », a sub-title that says what is being read,
 * and the gestures - « Retour au parcours », « Replier la relecture » (the
 * transcript takes the width, to read only) and the shortcut to the blank analyst.
 *
 * Under it, one sentence: nothing of the machine appears here, and the review is
 * shared.
 *
 * The first name is the person's, as typed in the field the experience marks for
 * it; without one, the page says « une personne » rather than inventing a name,
 * and a value that is an email is no name.
 */

export interface ReviewHeadProps {
  readonly session: SessionRow;
  /** The exchanges the transcript shows. */
  readonly exchanges: number;
  /** The review cannot be opened: only the way back is offered. */
  readonly blocked: boolean;
  readonly folded: boolean;
  readonly onFold: () => void;
  /** The shortcut to the analyst: absent without a grid, or when blocked. */
  readonly showTrial: boolean;
  readonly extractCount: number;
}

const ACTION_QUIET =
  'border-gray-200 px-3 text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/** « terminée le 9 oct. 2026 à 18:42 », « commencée le … » for a session that never ended. */
function statusLine(session: SessionRow): string {
  const word = sessionStatusLabel(session.status).toLowerCase();
  return session.endedAt === null
    ? `${word}, commencée le ${reviewFullDay(session.startedAt)}`
    : `${word} le ${reviewFullDay(session.endedAt)} à ${sessionTime(session.endedAt)}`;
}

export function ReviewHead({
  session,
  exchanges,
  blocked,
  folded,
  onFold,
  showTrial,
  extractCount,
}: ReviewHeadProps) {
  const first = personFirstName(session.personName?.value);
  const experience = [
    session.experienceName ?? 'Expérience retirée',
    session.experienceVersionNumber === null ? null : `v${session.experienceVersionNumber}`,
  ]
    .filter((part) => part !== null)
    .join(' ');
  const sub = [
    experience,
    session.protocolName === null ? null : `protocole ${session.protocolName}`,
    statusLine(session),
    exchanges === 0 ? 'Aucun échange' : plural(exchanges, 'échange'),
  ]
    .filter((part) => part !== null)
    .join(' · ');

  return (
    <div className="flex flex-col gap-4">
      <nav aria-label="Fil d’Ariane" className="flex flex-wrap gap-2 text-caption text-gray-600">
        <Link to="/admin/sessions" className="text-bleu-nuit hover:underline">
          Sessions
        </Link>
        <span aria-hidden="true">›</span>
        <Link
          to="/admin/sessions/$sessionId"
          params={{ sessionId: session.id }}
          className="text-bleu-nuit hover:underline"
        >
          Session du {reviewFullDay(session.startedAt)}
        </Link>
        <span aria-hidden="true">›</span>
        <span>Relecture</span>
      </nav>

      <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-[1_1_20rem] flex-col gap-1">
          <h1 className="font-bold text-bleu-nuit text-h3">
            Relecture · {first === null ? 'Session d’une personne' : `Session de ${first}`}
          </h1>
          <p className="text-gray-600 text-sm">{sub}</p>
        </div>
        <div className="flex max-w-full shrink-0 flex-wrap items-start justify-end gap-2">
          <Link
            to="/admin/sessions/$sessionId"
            params={{ sessionId: session.id }}
            className={`${HEAD_ACTION_SHAPE} ${ACTION_QUIET}`}
          >
            Retour au parcours
          </Link>
          {blocked ? null : (
            <button
              type="button"
              data-testid={REVIEW_TEST_IDS.fold}
              aria-pressed={folded}
              onClick={onFold}
              className={`${HEAD_ACTION_SHAPE} cursor-pointer ${ACTION_QUIET}`}
            >
              {folded ? 'Déplier la relecture' : 'Replier la relecture'}
            </button>
          )}
          {showTrial ? (
            <ReviewTrial
              sessionId={session.id}
              extractCount={extractCount}
              trashed={session.deletedAt !== null}
            />
          ) : null}
        </div>
      </div>

      {blocked ? null : (
        <p className="max-w-[75ch] text-caption text-gray-600">
          Rien de ce que la machine a produit n’apparaît ici. La relecture est partagée : chaque
          élément porte qui l’a posé et quand, et l’historique garde tous les gestes.
        </p>
      )}
    </div>
  );
}
